import { useState, useEffect } from "react";
import { Loader } from "lucide-react";
import api from "../lib/api";

const actionLabels: Record<string, string> = {
  login: "Innlogging",
  invite_user: "Invitert bruker",
  change_role: "Endret rolle",
  approve_sender: "Godkjent avsender",
  reject_invoice: "Avvist faktura",
  mark_fraud: "Markert svindel",
  undo_action: "Angret handling",
  remove_user: "Fjernet bruker",
};

const actionColors: Record<string, string> = {
  login: "bg-line text-night",
  invite_user: "bg-pos text-white",
  change_role: "bg-night text-white",
  approve_sender: "bg-pos text-white",
  reject_invoice: "bg-neg text-white",
  mark_fraud: "bg-neg text-white",
  undo_action: "bg-flag text-white",
  remove_user: "bg-neg text-white",
};

const dotColors: Record<string, string> = {
  login: "bg-faint",
  invite_user: "bg-pos",
  change_role: "bg-night",
  approve_sender: "bg-pos",
  reject_invoice: "bg-neg",
  mark_fraud: "bg-neg",
  undo_action: "bg-flag",
  remove_user: "bg-neg",
};

const actionTypes = [
  "login",
  "invite_user",
  "change_role",
  "approve_sender",
  "reject_invoice",
  "mark_fraud",
  "undo_action",
  "remove_user",
] as const;

const PAGE_SIZE = 50;

export default function AuditLog() {
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionFilter, setActionFilter] = useState("");
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const fetchLogs = async (newOffset: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(newOffset));
      if (actionFilter) params.set("action", actionFilter);
      const res = await api.get(`/audit/?${params}`);
      const data = Array.isArray(res.data) ? res.data : res.data.entries || [];
      setEntries(data);
      setHasMore(data.length === PAGE_SIZE);
      setOffset(newOffset);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(0);
  }, [actionFilter]);

  const formatTimestamp = (ts: string) => {
    if (!ts) return "\u2014";
    const date = ts.slice(0, 10);
    const time = ts.slice(11, 16);
    return `${date} ${time}`;
  };

  const formatDetails = (details: any) => {
    if (!details) return null;
    if (typeof details === "string") return details;
    return Object.entries(details)
      .map(([k, v]) => `${k}: ${v}`)
      .join(", ");
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-semibold text-faint uppercase tracking-widest">Aktivitetslogg</h2>
      </div>

      <div className="bg-white rounded-[14px] border border-line shadow-kpi p-6">
        <div className="flex items-center gap-3 mb-5">
          <p className="text-[12px] font-medium text-muted">Filter:</p>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="bg-canvas border border-line rounded-xl px-4 py-3 text-[13px] text-night focus:bg-white transition-all"
          >
            <option value="">Alle handlinger</option>
            {actionTypes.map((a) => (
              <option key={a} value={a}>
                {actionLabels[a]}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="w-5 h-5 animate-spin text-night" />
            <span className="ml-2 text-[13px] text-faint">Laster logg...</span>
          </div>
        ) : entries.length === 0 ? (
          <p className="text-[13px] text-faint text-center py-8">Ingen loggoppforinger funnet.</p>
        ) : (
          <div className="relative ml-4">
            {/* Vertical timeline line */}
            <div className="absolute left-0 top-0 bottom-0 w-px bg-line" />

            <div className="space-y-0">
              {entries.map((entry: any, i: number) => (
                <div key={entry.id || i} className="relative flex items-start gap-4 py-3 pl-6">
                  {/* Timeline dot */}
                  <div className={`absolute left-0 top-4 w-2.5 h-2.5 rounded-full -translate-x-1 ring-2 ring-white ${dotColors[entry.action] || "bg-faint"}`} />

                  <div className="flex-1 flex items-center justify-between gap-4 min-w-0">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span className="text-[12px] text-faint font-mono whitespace-nowrap">
                        {formatTimestamp(entry.timestamp || entry.created_at)}
                      </span>
                      <span className="text-[13px] text-night truncate">{entry.user_email || entry.user_name || "\u2014"}</span>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-md ${actionColors[entry.action] || "bg-night/[0.06] text-night"}`}>
                        {actionLabels[entry.action] || entry.action}
                      </span>
                      {formatDetails(entry.details) && (
                        <span className="text-[12px] text-faint hidden sm:inline">{formatDetails(entry.details)}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-center gap-3 mt-5">
          <button
            onClick={() => fetchLogs(Math.max(0, offset - PAGE_SIZE))}
            disabled={offset === 0}
            className="px-4 py-2.5 rounded-xl text-[13px] font-medium bg-white border border-line text-night hover:bg-canvas disabled:opacity-40 transition-all"
          >
            Forrige
          </button>
          <span className="text-[12px] text-faint">
            Viser {offset + 1}\u2013{offset + entries.length}
          </span>
          <button
            onClick={() => fetchLogs(offset + PAGE_SIZE)}
            disabled={!hasMore}
            className="px-4 py-2.5 rounded-xl text-[13px] font-medium bg-white border border-line text-night hover:bg-canvas disabled:opacity-40 transition-all"
          >
            Neste
          </button>
        </div>
      </div>
    </div>
  );
}
